import React, { useEffect, useState } from 'react';
import { XIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, MessageCircleIcon, PhoneIcon } from 'lucide-react';
interface PaymentProcessingBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  paymentMethod: string;
  onSuccess: () => void;
  onCancel: () => void;
  onFailure: (reason: string) => void;
  forceSuccess?: boolean;
}
type Stage = 'processing' | 'result';
type ResultScenario = 'success' | 'cancel' | 'failure' | 'pending';
type PendingSubScenario = 'verifying' | 'confirmed' | 'failed-verification' | 'manual-review' | 'payment-failed-review' | 'success-after-failure' | 'unable-to-confirm' | null;
export function PaymentProcessingBottomSheet({
  isOpen,
  onClose,
  paymentMethod,
  onSuccess,
  onCancel,
  onFailure,
  forceSuccess = false
}: PaymentProcessingBottomSheetProps) {
  const [stage, setStage] = useState<Stage>('processing');
  const [resultScenario, setResultScenario] = useState<ResultScenario>('success');
  const [pendingSubScenario, setPendingSubScenario] = useState<PendingSubScenario>(null);
  useEffect(() => {
    if (isOpen) {
      setStage('processing');
      setPendingSubScenario(null);
      // Simulate processing for 3 seconds
      setTimeout(() => {
        // Randomly select a scenario
        const scenarios: ResultScenario[] = ['success', 'cancel', 'failure', 'pending'];
        const randomScenario = forceSuccess ? 'success' : scenarios[Math.floor(Math.random() * scenarios.length)];
        setResultScenario(randomScenario);
        setStage('result');
        // If success, redirect after 2 seconds
        if (randomScenario === 'success') {
          setTimeout(() => {
            onSuccess();
          }, 2000);
        }
      }, 3000);
    }
  }, [forceSuccess, isOpen, onSuccess]);
  const handlePendingVerification = () => {
    setPendingSubScenario('verifying');
    // Wait 4 seconds before showing result
    setTimeout(() => {
      // Randomly select one of the 3 new scenarios
      const scenarios: PendingSubScenario[] = ['payment-failed-review', 'success-after-failure', 'unable-to-confirm'];
      const randomScenario = scenarios[Math.floor(Math.random() * scenarios.length)];
      setPendingSubScenario(randomScenario);
      // If success after failure, redirect after 2 seconds
      if (randomScenario === 'success-after-failure') {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    }, 4000);
  };
  if (!isOpen) return null;
  return <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[200] lg:hidden" onClick={onClose} data-id="element-1700" data-mp="1qc4q4am92qh3"></div>
      {/* Bottom Sheet */}
      <div className="fixed bottom-0 left-0 right-0 bg-white z-[201] rounded-t-3xl shadow-2xl h-[75vh] overflow-y-auto lg:hidden" data-id="element-1701" data-mp="2ivjzsgwff2yy">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-3xl" data-id="element-1702" data-mp="2e1yxicnsvmty">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-1703" data-mp="5jgyix4zqdb3">
            Payment Processing
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1704" data-mp="9ksq387fd8lx">
            <XIcon className="w-6 h-6 text-gray-600" data-id="element-1705" data-mp="eiu7zpt471vt" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6" data-id="element-1706" data-mp="ji75r5lixtd6">
          {/* Processing Stage */}
          {stage === 'processing' && <div className="text-center py-12" data-id="element-1707" data-mp="ogrykjyg5arl">
              <div className="relative mx-auto w-20 h-20 mb-6" data-id="element-1708" data-mp="taw6p6jg7ou9">
                <div className="absolute inset-0 rounded-full border-t-4 border-b-4 border-[#1E2B55] animate-spin" data-id="element-1709" data-mp="xb6jb21qxdi3"></div>
                <div className="absolute inset-3 rounded-full bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f]" data-id="element-1710" data-mp="12base70br5ya"></div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1711" data-mp="179vs85bnznm7">
                Processing Payment...
              </h3>
              <p className="text-gray-600 mb-2" data-id="element-1712" data-mp="tkj08v8ckcmm">
                Please wait while we process your payment
              </p>
              <p className="text-sm text-gray-500" data-id="element-1713" data-mp="13d85379e6s8z">
                Payment Method: {paymentMethod}
              </p>
            </div>}
          {/* Result Stage */}
          {stage === 'result' && <div data-id="element-1714" data-mp="an825dygbp8r">
              {/* Success Scenario */}
              {resultScenario === 'success' && <div className="text-center py-8" data-id="element-1715" data-mp="5pzo9pkc67db">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1716" data-mp="km798s3kjctn">
                    <CheckCircleIcon className="w-12 h-12 text-green-600" data-id="element-1717" data-mp="fmkxkn3e24my" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1718" data-mp="1w0ukrdvw9nc7">
                    Payment Successful! ✅
                  </h3>
                  <p className="text-gray-600 mb-2" data-id="element-1719" data-mp="1r1rfbsc05f3p">
                    Your payment has been processed successfully
                  </p>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-1720" data-mp="23kvetmwglzcr">
                    Redirecting to confirmation page...
                  </p>
                  <div className="flex justify-center" data-id="element-1721" data-mp="1pm5jabpag7qi">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" data-id="element-1722" data-mp="1tmpevau7r3aj"></div>
                  </div>
                </div>}
              {/* Cancel Scenario */}
              {resultScenario === 'cancel' && <div className="text-center py-8" data-id="element-1723" data-mp="2ma1qwr2qzspl">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1724" data-mp="28jy8ycawil5y">
                    <XIcon className="w-12 h-12 text-gray-600" data-id="element-1725" data-mp="2cl1n8udaw952" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1726" data-mp="30be4ivufri8b">
                    Payment Cancelled
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-1727" data-mp="352w3xfgt9ar6">
                    You have cancelled the payment process
                  </p>
                  <button onClick={onCancel} className="bg-[#1E2B55] hover:bg-[#162041] text-white font-bold py-3 px-8 rounded-lg transition-colors" data-id="element-1728" data-mp="3evqmumqr7p2p">
                    Return to Checkout
                  </button>
                </div>}
              {/* Failure Scenario */}
              {resultScenario === 'failure' && <div className="text-center py-8" data-id="element-1729" data-mp="2w13bd6c8et47">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1730" data-mp="2r80h0vzmrfmy">
                    <XCircleIcon className="w-12 h-12 text-red-600" data-id="element-1731" data-mp="2n7gj3q78g7zr" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1732" data-mp="2i8vg5j0lja7m">
                    Payment Failed
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-1733" data-mp="2daljf3z841jw">
                    We could not process your payment. Please try again or use a
                    different payment method.
                  </p>
                  <button onClick={() => onFailure('Payment processing failed. Please try again.')} className="bg-[#1E2B55] hover:bg-[#162041] text-white font-bold py-3 px-8 rounded-lg transition-colors" data-id="element-1734" data-mp="1y7k3qcaem2s6">
                    Try Again
                  </button>
                </div>}
              {/* Pending Scenario */}
              {resultScenario === 'pending' && !pendingSubScenario && <div className="text-center py-8" data-id="element-1735" data-mp="1pd5rkil678da">
                  <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1736" data-mp="1pcdv75jr16ly">
                    <ClockIcon className="w-12 h-12 text-yellow-600" data-id="element-1737" data-mp="1u5713wqyekzm" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1738" data-mp="wnyrhf62iapx">
                    Payment Pending ⏳
                  </h3>
                  <p className="text-gray-600 mb-2" data-id="element-1739" data-mp="2fu1nompjlz93">
                    Your payment is being verified
                  </p>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-1740" data-mp="2bt7xtnsjiadk">
                    This may take a few moments. Please do not close this
                    window.
                  </p>
                  <button onClick={handlePendingVerification} className="bg-[#1E2B55] hover:bg-[#162041] text-white font-bold py-3 px-8 rounded-lg transition-colors" data-id="element-1741" data-mp="2kte6mwdamm6e">
                    Verify Payment Status
                  </button>
                </div>}
              {/* Pending - Verifying */}
              {pendingSubScenario === 'verifying' && <div className="text-center py-8" data-id="element-1742" data-mp="33knl7k1x814u">
                  <div className="relative mx-auto w-20 h-20 mb-6" data-id="element-1743" data-mp="3igwem4z4rk9c">
                    <div className="absolute inset-0 rounded-full border-t-4 border-b-4 border-yellow-500 animate-spin" data-id="element-1744" data-mp="3ih4t21awixmn"></div>
                    <div className="absolute inset-3 rounded-full bg-yellow-100" data-id="element-1745" data-mp="3dfz1i6sz9gep"></div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1746" data-mp="4zsyxfhrh59m">
                    Verifying Payment...
                  </h3>
                  <p className="text-gray-600" data-id="element-1747" data-mp="3n4cotr9gp6zp">
                    Please wait while we verify your payment status
                  </p>
                </div>}
              {/* Payment Failed - Need Review */}
              {pendingSubScenario === 'payment-failed-review' && <div className="text-center py-8" data-id="element-1748" data-mp="3i7crc4qohck7">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1749" data-mp="3i85s8p2yh7uf">
                    <XCircleIcon className="w-12 h-12 text-red-600" data-id="element-1750" data-mp="39drcymifdxbb" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1751" data-mp="2ugqhq910zk83">
                    Payment Failed ❌
                  </h3>
                  <p className="text-gray-600 mb-2" data-id="element-1752" data-mp="2zes1kw69znkm">
                    The payment amount may be pending or deducted
                  </p>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-1753" data-mp="pp2e63sqhm7v">
                    Please contact your bank to review the transaction status
                  </p>
                  <div className="space-y-3 max-w-md mx-auto" data-id="element-1754" data-mp="fpv0tgm01rkr">
                    <a href="tel:+966530151938" className="flex items-center justify-center gap-2 w-full bg-[#1E2B55] hover:bg-[#162041] text-white font-bold py-3 px-6 rounded-lg transition-colors" data-id="element-1755" data-mp="knmsqamn9doy">
                      <PhoneIcon className="w-5 h-5" data-id="element-1756" data-mp="1dbsbwsy1uumo" />
                      Call Customer Service
                    </a>
                    <a href="https://wa.me/966530151938" target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-6 py-3 font-bold text-white transition-colors duration-150 hover:bg-brand-dark" data-id="element-1757" data-mp="yltfovq21t9u">
                      <MessageCircleIcon className="w-5 h-5" data-id="element-1758" data-mp="3grm8s2gy6rh4" />
                      Contact via WhatsApp
                    </a>
                    <button onClick={() => onFailure('Payment failed - amount may be pending')} className="w-full bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold py-3 px-6 rounded-lg transition-colors" data-id="element-1759" data-mp="3qja6gl43l4r1">
                      Try Another Payment Method
                    </button>
                  </div>
                </div>}
              {/* Success After Failure */}
              {pendingSubScenario === 'success-after-failure' && <div className="text-center py-8" data-id="element-1760" data-mp="32se5oyhnyz7n">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1761" data-mp="m4ip6n3oo5by">
                    <CheckCircleIcon className="w-12 h-12 text-green-600" data-id="element-1762" data-mp="i2f374je1icf" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1763" data-mp="3awvrs47z1r7j">
                    Payment Successful! ✅
                  </h3>
                  <p className="text-gray-600 mb-2" data-id="element-1764" data-mp="3fz4kr0kugt9n">
                    Your payment was verified and confirmed
                  </p>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-1765" data-mp="3oy866b7hhkrb">
                    Redirecting to confirmation page...
                  </p>
                  <div className="flex justify-center" data-id="element-1766" data-mp="2s90j2yr7v3la">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" data-id="element-1767" data-mp="2x6adytxdk7gi"></div>
                  </div>
                </div>}
              {/* Unable to Confirm - Manual Review */}
              {pendingSubScenario === 'unable-to-confirm' && <div className="text-center py-8" data-id="element-1768" data-mp="29gxwl6rw8t34">
                  <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1769" data-mp="29g6e8xnb4rus">
                    <ClockIcon className="w-12 h-12 text-orange-600" data-id="element-1770" data-mp="2dgq73p0ul9b4" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1771" data-mp="25y8wtgl5dqh3">
                    Unable to Confirm Payment ⏳
                  </h3>
                  <p className="text-gray-600 mb-2" data-id="element-1772" data-mp="1wy3s5e7c966m">
                    Payment will be confirmed manually
                  </p>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-1773" data-mp="1ry7nrduqbcqj">
                    Our customer service team will review your payment and
                    contact you shortly
                  </p>
                  <div className="space-y-3 max-w-md mx-auto" data-id="element-1774" data-mp="1i8eieul6jtuq">
                    <p className="text-sm font-medium text-gray-700 mb-3" data-id="element-1775" data-mp="1da3barm9rxg7">
                      Need immediate assistance? Contact us now:
                    </p>
                    <a href="tel:+966530151938" className="flex items-center justify-center gap-2 w-full bg-[#1E2B55] hover:bg-[#162041] text-white font-bold py-3 px-6 rounded-lg transition-colors" data-id="element-1776" data-mp="19am1xn3q2g5r">
                      <PhoneIcon className="w-5 h-5" data-id="element-1777" data-mp="2zdw5zzxa115e" />
                      Call Customer Service
                    </a>
                    <a href="https://wa.me/966530151938" target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-6 py-3 font-bold text-white transition-colors duration-150 hover:bg-brand-dark" data-id="element-1778" data-mp="34bei1z6lbwhm">
                      <MessageCircleIcon className="w-5 h-5" data-id="element-1779" data-mp="2ko6xtkqrnwvm" />
                      Contact via WhatsApp
                    </a>
                    <button onClick={onCancel} className="w-full bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold py-3 px-6 rounded-lg transition-colors" data-id="element-1780" data-mp="26pqm1fe1rg6m">
                      Return to Checkout
                    </button>
                  </div>
                </div>}
            </div>}
        </div>
      </div>
    </>;
}